import type { GameState } from './GameState.js';
import type { RuleSet } from './rules/types.js';

export type DrawReason =
  | 'quiet-move-limit'
  | 'threefold-repetition'
  | 'insufficient-material';

export class DrawRules {
  constructor(private readonly ruleSet: RuleSet) {}

  detect(state: GameState): DrawReason | null {
    if (state.quietMoveCount >= this.ruleSet.drawAfterQuietMoves) {
      return 'quiet-move-limit';
    }
    if (this.isThreefoldRepetition(state)) {
      return 'threefold-repetition';
    }
    if (this.isInsufficientMaterial(state)) {
      return 'insufficient-material';
    }
    return null;
  }

  private isThreefoldRepetition(state: GameState): boolean {
    const counts = new Map<string, number>();
    for (const hash of state.positionHistory) {
      const next = (counts.get(hash) ?? 0) + 1;
      if (next >= 3) return true;
      counts.set(hash, next);
    }
    return false;
  }

  private isInsufficientMaterial(state: GameState): boolean {
    let whiteMen = 0;
    let whiteKings = 0;
    let blackMen = 0;
    let blackKings = 0;
    for (const { piece } of state.pieces()) {
      if (piece.color === 'white') {
        if (piece.kind === 'king') whiteKings += 1;
        else whiteMen += 1;
      } else {
        if (piece.kind === 'king') blackKings += 1;
        else blackMen += 1;
      }
    }
    // King-vs-king is the canonical Russian shashki KvK draw. Extra-piece
    // endings (KvKK, etc.) remain ongoing here — the quiet-move counter or
    // threefold repetition catches the rest.
    return (
      whiteMen === 0 && blackMen === 0 && whiteKings === 1 && blackKings === 1
    );
  }
}
